<template>
  <v-dialog :model-value="true" max-width="800px" persistent scrollable>
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon start>mdi-swap-horizontal</v-icon>
        Cerere de Portabilitate
        <v-spacer />
        <v-btn icon variant="text" color="white" @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <v-stepper v-model="step" :items="stepItems" alt-labels>
          <!-- Step 1: SIM Destinatie (retea proprie) -->
          <template #[`item.1`]>
            <v-card flat>
              <v-card-title class="text-h6 px-0">SIM Destinatie</v-card-title>
              <v-card-text class="px-0">
                <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                  Introdu numarul de telefon din reteaua noastra pe care se va porta numarul dorit.
                  Trebuie sa ai deja un SIM activ in reteaua Veridian/Goil.
                </v-alert>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.recipient_msisdn"
                      label="Numar SIM din reteaua proprie *"
                      placeholder="07xxxxxxxx"
                      prepend-inner-icon="mdi-sim"
                      :rules="[rules.required, rules.phone]"
                      variant="outlined"
                      density="compact"
                      :disabled="recipientVerified"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div v-if="!recipientVerified">
                      <v-btn
                        color="warning"
                        :loading="sendingRecipientCode"
                        :disabled="!isValidPhone(form.recipient_msisdn) || recipientCooldown > 0"
                        @click="sendRecipientCode"
                      >
                        <v-icon start>mdi-message-text</v-icon>
                        {{ recipientCooldown > 0 ? `Asteapta ${recipientCooldown}s` : 'Trimite Cod SMS' }}
                      </v-btn>
                    </div>
                    <v-chip v-else color="success" size="small">
                      <v-icon start>mdi-check</v-icon>
                      Verificat
                    </v-chip>
                  </v-col>
                </v-row>

                <!-- OTP pentru recipient -->
                <v-row v-if="showRecipientOtp && !recipientVerified">
                  <v-col cols="12">
                    <div class="d-flex align-center ga-3">
                      <v-otp-input
                        v-model="recipientOtpCode"
                        length="6"
                        type="number"
                        :disabled="verifyingRecipientCode"
                      />
                      <v-btn
                        color="primary"
                        :loading="verifyingRecipientCode"
                        :disabled="recipientOtpCode.length !== 6"
                        @click="verifyRecipientCode"
                      >
                        Verifica
                      </v-btn>
                    </div>
                    <v-alert v-if="recipientError" type="error" density="compact" class="mt-2">
                      {{ recipientError }}
                    </v-alert>
                  </v-col>
                </v-row>

                <v-divider class="my-4" />

                <v-card variant="outlined" color="warning" class="mt-2">
                  <v-card-text class="text-center pa-6">
                    <v-icon size="48" color="warning" class="mb-3">mdi-sim-alert</v-icon>
                    <div class="text-h6 mb-2">Nu ai un SIM in reteaua noastra?</div>
                    <div class="text-body-2 text-grey mb-4">
                      Pentru a porta un numar, ai nevoie de o cartela SIM activa in reteaua Veridian/Goil.
                      Achizitioneaza una acum si revino sa completezi cererea de portare.
                    </div>
                    <v-btn
                      color="warning"
                      variant="flat"
                      size="large"
                      @click="showBuyMobileDialog = true"
                    >
                      <v-icon start>mdi-cart</v-icon>
                      Cumpara un SIM Prepaid
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 2: Verificare numere de portat -->
          <template #[`item.2`]>
            <v-card flat>
              <v-card-title class="text-h6 px-0">Numar de Portat</v-card-title>
              <v-card-text class="px-0">
                <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                  Introdu numarul de telefon pe care vrei sa il portezi din alta retea.
                </v-alert>

                <!-- Numar de portat (destination) -->
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.destination_msisdn"
                      label="Numar de telefon de portat *"
                      placeholder="07xxxxxxxx"
                      prepend-inner-icon="mdi-phone"
                      :rules="[rules.required, rules.phone]"
                      variant="outlined"
                      density="compact"
                      :disabled="msisdn !== null"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <div v-if="!destinationVerified">
                      <v-btn
                        color="warning"
                        :loading="sendingDestCode"
                        :disabled="!isValidPhone(form.destination_msisdn) || destCooldown > 0"
                        @click="sendDestinationCode"
                      >
                        <v-icon start>mdi-message-text</v-icon>
                        {{ destCooldown > 0 ? `Asteapta ${destCooldown}s` : 'Trimite Cod SMS' }}
                      </v-btn>
                    </div>
                    <v-chip v-else color="success" size="small">
                      <v-icon start>mdi-check</v-icon>
                      Verificat
                    </v-chip>
                  </v-col>
                </v-row>

                <!-- OTP pentru destination -->
                <v-row v-if="showDestOtp && !destinationVerified">
                  <v-col cols="12">
                    <div class="d-flex align-center ga-3">
                      <v-otp-input
                        v-model="destOtpCode"
                        length="6"
                        type="number"
                        :disabled="verifyingDestCode"
                      />
                      <v-btn
                        color="primary"
                        :loading="verifyingDestCode"
                        :disabled="destOtpCode.length !== 6"
                        @click="verifyDestinationCode"
                      >
                        Verifica
                      </v-btn>
                    </div>
                    <v-alert v-if="destError" type="error" density="compact" class="mt-2">
                      {{ destError }}
                    </v-alert>
                  </v-col>
                </v-row>

                <v-divider class="my-4" />

                <!-- Operator donor -->
                <v-select
                  v-model="form.port_operator_id"
                  :items="portOperators"
                  item-value="id"
                  item-title="name"
                  label="Operator de la care portezi (operator donor) *"
                  prepend-inner-icon="mdi-antenna"
                  :rules="[rules.required]"
                  variant="outlined"
                  density="compact"
                />

                <!-- Tip abonament la donor -->
                <v-radio-group
                  v-model="form.op_donor_subscription_type_id"
                  inline
                  label="Tip abonament la operatorul donor *"
                  :rules="[rules.required]"
                >
                  <v-radio :value="1" label="Prepaid (Cartela)" />
                  <v-radio :value="2" label="Postpaid (Abonament)" />
                </v-radio-group>

                <!-- Serial SIM pentru prepaid -->
                <v-text-field
                  v-if="form.op_donor_subscription_type_id === 1"
                  v-model="form.serial_sim_donor"
                  label="Serial SIM de la operatorul donor *"
                  hint="Serialul se gaseste pe cartela SIM sau pe ambalaj"
                  persistent-hint
                  prepend-inner-icon="mdi-sim"
                  variant="outlined"
                  density="compact"
                  :rules="[rules.required]"
                />

                <!-- Cod client pentru postpaid -->
                <v-text-field
                  v-if="form.op_donor_subscription_type_id === 2"
                  v-model="form.donor_client_code"
                  label="Codul Clientului la operatorul donor *"
                  hint="Codul de client/abonat de la operatorul de la care portezi"
                  persistent-hint
                  prepend-inner-icon="mdi-card-account-details"
                  variant="outlined"
                  density="compact"
                  :rules="[rules.required]"
                />
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 3: Date personale -->
          <template #[`item.3`]>
            <v-card flat>
              <v-card-title class="text-h6 px-0">Date Personale</v-card-title>
              <v-card-text class="px-0">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.name"
                      label="Nume si prenume *"
                      prepend-inner-icon="mdi-account"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.cnp"
                      label="CNP *"
                      prepend-inner-icon="mdi-card-account-details"
                      :rules="[rules.required, rules.cnp]"
                      variant="outlined"
                      density="compact"
                      maxlength="13"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="form.id_type_id"
                      :items="idTypes"
                      item-value="id"
                      item-title="name"
                      label="Tip act identitate *"
                      prepend-inner-icon="mdi-card-account-details-outline"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.id_serial"
                      label="Serie CI *"
                      placeholder="AB"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="compact"
                      maxlength="2"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="form.serial_ci"
                      label="Numar CI *"
                      placeholder="123456"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="compact"
                      maxlength="6"
                    />
                  </v-col>
                </v-row>

                <v-text-field
                  v-model="form.address"
                  label="Adresa completa *"
                  prepend-inner-icon="mdi-map-marker"
                  :rules="[rules.required]"
                  variant="outlined"
                  density="compact"
                />

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.city"
                      label="Localitate *"
                      prepend-inner-icon="mdi-city"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.county"
                      label="Judet *"
                      prepend-inner-icon="mdi-map"
                      :rules="[rules.required]"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.email"
                      label="Email *"
                      prepend-inner-icon="mdi-email"
                      :rules="[rules.required, rules.email]"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.contact_phone"
                      label="Telefon de contact *"
                      prepend-inner-icon="mdi-phone"
                      :rules="[rules.required, rules.phone]"
                      variant="outlined"
                      density="compact"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 4: Documente -->
          <template #[`item.4`]>
            <v-card flat>
              <v-card-title class="text-h6 px-0">Documente</v-card-title>
              <v-card-text class="px-0">
                <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                  Incarca documentele necesare pentru cererea de portabilitate. Copia dupa actul de identitate este obligatorie.
                </v-alert>

                <!-- Copie CI (obligatoriu) -->
                <v-file-input
                  v-model="files.id_document"
                  label="Copie act identitate (CI/Pasaport) *"
                  prepend-icon="mdi-card-account-details"
                  accept="image/*,.pdf"
                  :rules="[rules.required]"
                  variant="outlined"
                  density="compact"
                  show-size
                  class="mb-2"
                />

                <!-- Ultima factura (optional) -->
                <v-file-input
                  v-model="files.last_invoice"
                  label="Ultima factura de la operatorul donor (optional)"
                  prepend-icon="mdi-file-document-outline"
                  accept="image/*,.pdf"
                  variant="outlined"
                  density="compact"
                  show-size
                  clearable
                  class="mb-2"
                />

                <!-- Imputernicire (optional) -->
                <v-file-input
                  v-model="files.power_of_attorney"
                  label="Imputernicire (optional)"
                  prepend-icon="mdi-file-sign"
                  accept="image/*,.pdf"
                  variant="outlined"
                  density="compact"
                  show-size
                  clearable
                />
              </v-card-text>
            </v-card>
          </template>

          <!-- Step 5: Confirmare -->
          <template #[`item.5`]>
            <v-card flat>
              <v-card-title class="text-h6 px-0">Confirmare Cerere</v-card-title>
              <v-card-text class="px-0">
                <v-alert type="info" variant="tonal" class="mb-4">
                  Verifica datele introduse inainte de a trimite cererea.
                </v-alert>

                <v-card variant="outlined" class="mb-4">
                  <v-card-subtitle class="bg-grey-lighten-3">SIM Destinatie</v-card-subtitle>
                  <v-card-text>
                    <v-row dense>
                      <v-col cols="6"><strong>Numar SIM retea proprie:</strong></v-col>
                      <v-col cols="6">{{ form.recipient_msisdn }}</v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card variant="outlined" class="mb-4">
                  <v-card-subtitle class="bg-grey-lighten-3">Date Portare</v-card-subtitle>
                  <v-card-text>
                    <v-row dense>
                      <v-col cols="6"><strong>Numar de portat:</strong></v-col>
                      <v-col cols="6">{{ form.destination_msisdn }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>Operator donor:</strong></v-col>
                      <v-col cols="6">{{ getOperatorName(form.port_operator_id) }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>Tip abonament:</strong></v-col>
                      <v-col cols="6">{{ form.op_donor_subscription_type_id === 1 ? 'Prepaid' : 'Postpaid' }}</v-col>
                    </v-row>
                    <v-row dense v-if="form.serial_sim_donor">
                      <v-col cols="6"><strong>Serial SIM:</strong></v-col>
                      <v-col cols="6">{{ form.serial_sim_donor }}</v-col>
                    </v-row>
                    <v-row dense v-if="form.donor_client_code">
                      <v-col cols="6"><strong>Cod Client:</strong></v-col>
                      <v-col cols="6">{{ form.donor_client_code }}</v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card variant="outlined" class="mb-4">
                  <v-card-subtitle class="bg-grey-lighten-3">Date Personale</v-card-subtitle>
                  <v-card-text>
                    <v-row dense>
                      <v-col cols="6"><strong>Nume:</strong></v-col>
                      <v-col cols="6">{{ form.name }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>CNP:</strong></v-col>
                      <v-col cols="6">{{ form.cnp }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>Act identitate:</strong></v-col>
                      <v-col cols="6">{{ form.id_serial }} {{ form.serial_ci }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>Adresa:</strong></v-col>
                      <v-col cols="6">{{ form.address }}, {{ form.city }}, {{ form.county }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>Email:</strong></v-col>
                      <v-col cols="6">{{ form.email }}</v-col>
                    </v-row>
                    <v-row dense>
                      <v-col cols="6"><strong>Telefon contact:</strong></v-col>
                      <v-col cols="6">{{ form.contact_phone }}</v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-card variant="outlined" class="mb-4">
                  <v-card-subtitle class="bg-grey-lighten-3">Documente Atasate</v-card-subtitle>
                  <v-card-text>
                    <v-row dense>
                      <v-col cols="6"><strong>Copie CI:</strong></v-col>
                      <v-col cols="6">{{ idDocFile?.name || '-' }}</v-col>
                    </v-row>
                    <v-row dense v-if="lastInvoiceFile">
                      <v-col cols="6"><strong>Ultima factura:</strong></v-col>
                      <v-col cols="6">{{ lastInvoiceFile.name }}</v-col>
                    </v-row>
                    <v-row dense v-if="poaFile">
                      <v-col cols="6"><strong>Imputernicire:</strong></v-col>
                      <v-col cols="6">{{ poaFile.name }}</v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <v-checkbox
                  v-model="acceptTerms"
                  label="Confirm ca datele introduse sunt corecte si accept termenii si conditiile de portare."
                  :rules="[v => !!v || 'Trebuie sa accepti termenii']"
                  color="primary"
                />
              </v-card-text>
            </v-card>
          </template>
        </v-stepper>

        <v-alert v-if="submitError" type="error" density="compact" class="mt-4">
          {{ submitError }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-btn v-if="step > 1" variant="text" @click="step--">
          <v-icon start>mdi-arrow-left</v-icon>
          Inapoi
        </v-btn>
        <v-spacer />
        <v-btn variant="text" @click="$emit('close')">Anuleaza</v-btn>
        <v-btn
          v-if="step < 5"
          color="primary"
          :disabled="!canProceed"
          @click="step++"
        >
          Continua
          <v-icon end>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="primary"
          :loading="submitting"
          :disabled="!acceptTerms"
          @click="submitRequest"
        >
          <v-icon start>mdi-send</v-icon>
          Trimite Cererea
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Buy SIM Prepaid Dialog -->
    <BuyNewSIMCardService
      v-if="showBuyMobileDialog"
      @close="showBuyMobileDialog = false"
      @newServiceAdded="showBuyMobileDialog = false"
    />
  </v-dialog>
</template>

<script>
import { log } from "@/composables/useLog";
import {
  getPortRequestOptions,
  createEndUserPortRequest,
  uploadPortRequestDocuments,
  sendPortabilityVerificationCode,
  verifyPortabilityCode
} from "@/composables/usePortRequestsAPI";
import BuyNewSIMCardService from "@/components/BuyNewSIMCardService.vue";

export default {
  name: 'CreatePortabilityRequestDialog',
  components: { BuyNewSIMCardService },
  props: {
    token: {
      type: String,
      required: true
    },
    msisdn: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'created'],
  data() {
    return {
      step: 1,
      stepItems: [
        { title: 'SIM Destinatie', value: 1 },
        { title: 'Numar Portat', value: 2 },
        { title: 'Date Personale', value: 3 },
        { title: 'Documente', value: 4 },
        { title: 'Confirmare', value: 5 }
      ],

      form: {
        recipient_msisdn: '',
        destination_msisdn: '',
        port_operator_id: null,
        op_donor_subscription_type_id: null,
        serial_sim_donor: '',
        donor_client_code: '',
        name: '',
        cnp: '',
        id_type_id: null,
        id_serial: '',
        serial_ci: '',
        address: '',
        city: '',
        county: '',
        email: '',
        contact_phone: ''
      },

      // Options
      portOperators: [],
      idTypes: [
        { id: 1, name: 'Carte de Identitate' },
        { id: 2, name: 'Pasaport' },
        { id: 3, name: 'Permis de sedere' }
      ],

      // Recipient (own network) verification state
      recipientVerified: false,
      showRecipientOtp: false,
      recipientOtpCode: '',
      sendingRecipientCode: false,
      verifyingRecipientCode: false,
      recipientCooldown: 0,
      recipientError: null,
      recipientCooldownTimer: null,

      // Destination (other network) verification state
      destinationVerified: false,
      showDestOtp: false,
      destOtpCode: '',
      sendingDestCode: false,
      verifyingDestCode: false,
      destCooldown: 0,
      destError: null,
      destCooldownTimer: null,

      // Document files
      files: {
        id_document: null,
        last_invoice: null,
        power_of_attorney: null
      },

      // Buy prepaid dialog
      showBuyMobileDialog: false,

      // Form state
      acceptTerms: false,
      submitting: false,
      submitError: null,

      // Validation rules
      rules: {
        required: v => !!v || 'Camp obligatoriu',
        phone: v => /^(\+?40|0)?7\d{8}$/.test(v?.replace(/\s/g, '')) || 'Numar de telefon invalid',
        email: v => /.+@.+\..+/.test(v) || 'Email invalid',
        cnp: v => /^\d{13}$/.test(v) || 'CNP invalid (13 cifre)'
      }
    };
  },
  computed: {
    idDocFile() {
      return Array.isArray(this.files.id_document) ? this.files.id_document[0] : this.files.id_document;
    },
    lastInvoiceFile() {
      return Array.isArray(this.files.last_invoice) ? this.files.last_invoice[0] : this.files.last_invoice;
    },
    poaFile() {
      return Array.isArray(this.files.power_of_attorney) ? this.files.power_of_attorney[0] : this.files.power_of_attorney;
    },
    canProceed() {
      if (this.step === 1) {
        return this.recipientVerified;
      }
      if (this.step === 2) {
        return this.destinationVerified &&
               this.form.port_operator_id &&
               this.form.op_donor_subscription_type_id &&
               (this.form.op_donor_subscription_type_id !== 1 || this.form.serial_sim_donor) &&
               (this.form.op_donor_subscription_type_id !== 2 || this.form.donor_client_code);
      }
      if (this.step === 3) {
        return this.form.name &&
               this.form.cnp && /^\d{13}$/.test(this.form.cnp) &&
               this.form.id_type_id &&
               this.form.id_serial &&
               this.form.serial_ci &&
               this.form.address &&
               this.form.city &&
               this.form.county &&
               this.form.email && /.+@.+\..+/.test(this.form.email) &&
               this.form.contact_phone;
      }
      if (this.step === 4) {
        const idDoc = Array.isArray(this.files.id_document) ? this.files.id_document[0] : this.files.id_document;
        return !!idDoc;
      }
      return true;
    }
  },
  methods: {
    isValidPhone(phone) {
      if (!phone) return false;
      return /^(\+?40|0)?7\d{8}$/.test(phone.replace(/\s/g, ''));
    },

    async loadOptions() {
      try {
        const response = await getPortRequestOptions(this.token);
        if (!response.error) {
          this.portOperators = response.port_operators || [];
          if (response.id_types?.length) {
            this.idTypes = response.id_types;
          }
        }
      } catch (err) {
        log("Error loading options:", err);
      }
    },

    // ===== Recipient (own network) verification =====
    async sendRecipientCode() {
      this.sendingRecipientCode = true;
      this.recipientError = null;

      try {
        const response = await sendPortabilityVerificationCode(
          this.token,
          this.form.recipient_msisdn,
          'recipient'
        );

        if (response.error) {
          this.recipientError = response.message;
        } else {
          this.showRecipientOtp = true;
          this.recipientCooldown = 60;
          this.startRecipientCooldownTimer();
        }
      } catch (err) {
        this.recipientError = "Eroare la trimiterea codului";
      } finally {
        this.sendingRecipientCode = false;
      }
    },

    async verifyRecipientCode() {
      this.verifyingRecipientCode = true;
      this.recipientError = null;

      try {
        const response = await verifyPortabilityCode(
          this.token,
          this.form.recipient_msisdn,
          this.recipientOtpCode,
          'recipient'
        );

        if (response.error) {
          this.recipientError = response.message;
          this.recipientOtpCode = '';
        } else {
          this.recipientVerified = true;
          this.showRecipientOtp = false;
        }
      } catch (err) {
        this.recipientError = "Eroare la verificare";
      } finally {
        this.verifyingRecipientCode = false;
      }
    },

    startRecipientCooldownTimer() {
      this.stopRecipientCooldownTimer();
      this.recipientCooldownTimer = setInterval(() => {
        if (this.recipientCooldown > 0) {
          this.recipientCooldown--;
        } else {
          this.stopRecipientCooldownTimer();
        }
      }, 1000);
    },

    stopRecipientCooldownTimer() {
      if (this.recipientCooldownTimer) {
        clearInterval(this.recipientCooldownTimer);
        this.recipientCooldownTimer = null;
      }
    },

    // ===== Destination (other network) verification =====
    async sendDestinationCode() {
      this.sendingDestCode = true;
      this.destError = null;

      try {
        const response = await sendPortabilityVerificationCode(
          this.token,
          this.form.destination_msisdn,
          'destination'
        );

        if (response.error) {
          this.destError = response.message;
        } else {
          this.showDestOtp = true;
          this.destCooldown = 60;
          this.startDestCooldownTimer();
        }
      } catch (err) {
        this.destError = "Eroare la trimiterea codului";
      } finally {
        this.sendingDestCode = false;
      }
    },

    async verifyDestinationCode() {
      this.verifyingDestCode = true;
      this.destError = null;

      try {
        const response = await verifyPortabilityCode(
          this.token,
          this.form.destination_msisdn,
          this.destOtpCode,
          'destination'
        );

        if (response.error) {
          this.destError = response.message;
          this.destOtpCode = '';
        } else {
          this.destinationVerified = true;
          this.showDestOtp = false;
        }
      } catch (err) {
        this.destError = "Eroare la verificare";
      } finally {
        this.verifyingDestCode = false;
      }
    },

    startDestCooldownTimer() {
      this.stopDestCooldownTimer();
      this.destCooldownTimer = setInterval(() => {
        if (this.destCooldown > 0) {
          this.destCooldown--;
        } else {
          this.stopDestCooldownTimer();
        }
      }, 1000);
    },

    stopDestCooldownTimer() {
      if (this.destCooldownTimer) {
        clearInterval(this.destCooldownTimer);
        this.destCooldownTimer = null;
      }
    },

    getOperatorName(id) {
      const op = this.portOperators.find(o => o.id === id);
      return op?.name || '-';
    },

    async submitRequest() {
      if (!this.acceptTerms) return;

      this.submitting = true;
      this.submitError = null;

      try {
        const response = await createEndUserPortRequest(this.token, {
          recipient_msisdn: this.form.recipient_msisdn,
          destination_msisdn: this.form.destination_msisdn,
          port_operator_id: this.form.port_operator_id,
          op_donor_subscription_type_id: this.form.op_donor_subscription_type_id,
          serial_sim_donor: this.form.serial_sim_donor,
          donor_client_code: this.form.donor_client_code,
          name: this.form.name,
          cnp: this.form.cnp,
          id_type_id: this.form.id_type_id,
          id_serial: this.form.id_serial,
          serial_ci: this.form.serial_ci,
          address: this.form.address,
          city: this.form.city,
          county: this.form.county,
          email: this.form.email,
          contact_phone: this.form.contact_phone
        });

        if (response.error) {
          this.submitError = response.message;
        } else {
          // Upload documents if port request was created successfully
          const portRequestId = response.port_request?.id || response.id;
          const idDoc = Array.isArray(this.files.id_document) ? this.files.id_document[0] : this.files.id_document;
          const lastInv = Array.isArray(this.files.last_invoice) ? this.files.last_invoice[0] : this.files.last_invoice;
          const poa = Array.isArray(this.files.power_of_attorney) ? this.files.power_of_attorney[0] : this.files.power_of_attorney;
          if (portRequestId && idDoc) {
            const uploadResult = await uploadPortRequestDocuments(
              this.token,
              portRequestId,
              {
                id_document: idDoc,
                last_invoice: lastInv || null,
                power_of_attorney: poa || null
              }
            );
            if (uploadResult.error) {
              log("Warning: Documents upload failed:", uploadResult.message);
            }
          }
          this.$emit('created', response);
        }
      } catch (err) {
        log("Error submitting port request:", err);
        this.submitError = "Eroare la trimiterea cererii";
      } finally {
        this.submitting = false;
      }
    }
  },
  mounted() {
    this.loadOptions();

    // If msisdn prop is provided, pre-fill destination and mark as verified
    if (this.msisdn) {
      this.form.destination_msisdn = this.msisdn.msisdn;
      this.destinationVerified = this.msisdn.verified_at ? true : false;
    }
  },
  beforeUnmount() {
    this.stopRecipientCooldownTimer();
    this.stopDestCooldownTimer();
  }
};
</script>
